// src/pages/Admin/create/CreateHub.tsx
import React, { useState } from "react";
import { Env, ENV_COLORS, getAdminApiUrl, Spinner, ErrorBanner, SuccessBanner } from "../shared";
import { Deal } from "../deals/api/useDeals";
import { useCreateDeal } from "../deals/api/useCreateDeals";
import { EntityRail, EntityType } from "./rail/EntityRail";
import { RecentDealsList } from "./rail/RecentDealsList";
import { DealForm } from "./deal/DealForm";
import { DealReview } from "./deal/DealReview";
import { useDealFormState, draftFromDeal, blankDealForm } from "./deal/useDealFormState";
import { PickedBusiness } from "./shared/BusinessPicker";

export interface CreateIntent {
  type: EntityType;
  env?: Env;
  business?: PickedBusiness;
}

export function CreateHub({ initialEnv = "prod", intent }: { initialEnv?: Env; intent?: CreateIntent }) {
  const [env, setEnv] = useState<Env>(intent?.env ?? initialEnv);
  const [entity, setEntity] = useState<EntityType>(intent?.type ?? "deal");
  const [reviewing, setReviewing] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const { form, setField, reset, canSubmit, buildPayload } = useDealFormState(
    intent?.business ? { ...blankDealForm(), business: intent.business } : undefined,
  );
  const { create, loading, error } = useCreateDeal();

  const handlePickRecent = (deal: Deal) => {
    reset(draftFromDeal(deal));
    setReviewing(false);
  };

  const handleSubmit = async () => {
    const result = await create(buildPayload(env));
    if (!result) return;
    setSuccessMsg("Deal created successfully.");
    setTimeout(() => setSuccessMsg(null), 3000);
    reset(blankDealForm());
    setReviewing(false);
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Top bar: env switcher */}
      <div className="flex items-center gap-3 flex-wrap">
        <h2 className="text-lg font-semibold text-white mr-auto">Create</h2>
        <div className="flex items-center gap-1.5 p-1 bg-gray-800/80 border border-gray-700 rounded-xl">
          {(["dev", "staging", "prod"] as Env[]).map(e => {
            const configured = Boolean(getAdminApiUrl(e));
            const isActive = env === e;
            return (
              <button
                key={e}
                onClick={() => {
                  if (!configured || e === env) return;
                  setEnv(e);
                  if (form.business) setField("business", null);
                  setReviewing(false);
                }}
                disabled={!configured}
                className={`px-3 py-1 text-xs font-medium rounded-lg border transition-all capitalize ${
                  isActive ? `${ENV_COLORS[e].pill} border-opacity-100`
                    : configured ? "text-gray-400 border-transparent hover:text-gray-200 hover:bg-gray-700/60"
                      : "text-gray-600 border-transparent cursor-not-allowed opacity-40"
                }`}
              >
                {e}
              </button>
            );
          })}
        </div>
        {env === "prod" && (
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-semibold tracking-wide ${ENV_COLORS.prod.badge}`}>
            <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />PRODUCTION
          </span>
        )}
      </div>

      {successMsg && <SuccessBanner message={successMsg} />}
      {error && <ErrorBanner message={error} />}

      {/* Two-pane studio */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="sm:w-[280px] flex-shrink-0 bg-gray-900 border border-gray-800 rounded-2xl p-4">
          <EntityRail active={entity} onSelect={setEntity} />
          <RecentDealsList env={env} onPick={handlePickRecent} />
        </div>

        <div className="flex-1 bg-gray-900 border border-gray-800 rounded-2xl p-5">
          {entity === "deal" && (
            <>
              {reviewing ? <DealReview env={env} form={form} /> : <DealForm env={env} form={form} setField={setField} />}
              <div className="flex gap-3 mt-6 pt-4 border-t border-gray-800">
                {reviewing && (
                  <button onClick={() => setReviewing(false)} disabled={loading}
                    className="px-4 py-2.5 text-sm font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 rounded-xl transition-all disabled:opacity-50">
                    Back
                  </button>
                )}
                <div className="flex-1" />
                <button
                  onClick={reviewing ? handleSubmit : () => setReviewing(true)}
                  disabled={loading || !canSubmit}
                  className="px-6 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-violet-600 to-pink-600 rounded-xl hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 min-w-[120px]"
                >
                  {loading ? <><Spinner size="sm" />Creating…</> : reviewing ? "Create Deal" : "Review →"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
